import { useCommentEditing } from "@/features/controls/hooks/use-comment-editing";
import type { ControlResultActivityListItem } from "@/models/control-result-activity.model";
import { showAlertCallout } from "@/shared/alerts";
import { useConfirmPopoverStore } from "@/shared/stores";
import {
  getControlResultAttachmentUrl,
  getGetControlResultAttachmentsQueryKey,
  useDeleteComment,
} from "@/spz-api/control-result-comments/control-result-comments";
import {
  getGetControlResultActivitiesQueryKey,
  getGetControlResultListPaginatedQueryKey,
} from "@/spz-api/control-results/control-results";
import type { UserId } from "@/spz-api/model";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useControlResultActivityListing } from "../hooks/use-control-result-activity-listing";

type UseResultDetailActivityListingProps = {
  controlId: string;
  controlResultId: string;
  userId: UserId;
};

export const useResultDetailActivityListingViewModel = ({
  controlId,
  controlResultId,
  userId,
}: UseResultDetailActivityListingProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { openPopover, closePopover } = useConfirmPopoverStore();

  // Fetch activity list
  const { data: activityList, isLoading } = useControlResultActivityListing(
    controlId,
    controlResultId,
  );

  // Comment editing state
  const {
    isEditing,
    editingCommentId,
    handleEdit,
    handleCancel,
    handleValidate,
    updateContent,
  } = useCommentEditing(controlId, controlResultId);

  // Delete mutation
  const { mutate: deleteCommentMutate } = useDeleteComment();

  // Handle comment/attachment deletion
  const handleDeletion = (commentId: string, attachmentOnly: boolean) => {
    deleteCommentMutate(
      {
        controlId,
        controlResultId,
        commentId,
      },
      {
        onSuccess: () => {
          const message = attachmentOnly
            ? t("success_attachment_deleted")
            : t("success_comment_deleted");

          showAlertCallout({
            type: "success",
            title: message,
          });

          closePopover();

          // Invalidate relevant queries
          queryClient.invalidateQueries({
            queryKey: getGetControlResultActivitiesQueryKey(
              controlId,
              controlResultId,
            ),
          });
          queryClient.invalidateQueries({
            queryKey: getGetControlResultListPaginatedQueryKey(controlId, {}),
          });
          queryClient.invalidateQueries({
            queryKey: getGetControlResultAttachmentsQueryKey(
              controlId,
              controlResultId,
            ),
          });
        },
        onError: () => {
          showAlertCallout({
            type: "critical",
            title: t("error_comment_delete"),
          });

          closePopover();
        },
      },
    );
  };

  // Delete handler with confirmation
  const onDelete = (
    item: ControlResultActivityListItem,
    anchor: HTMLElement,
  ) => {
    if (!anchor) return;

    const description = item.attachmentOnly
      ? t("content_delete_attachment")
      : t("deletion_confirmation");

    openPopover({
      description,
      onConfirm: () => {
        if (!item.comment) return;
        handleDeletion(item.comment.id, item.attachmentOnly);
      },
      anchorElement: anchor,
    });
  };

  // Cancel editing handler with confirmation
  const onCancel = (anchor: HTMLElement) => {
    if (!anchor) return;

    openPopover({
      description: t("content_cancel_modification"),
      onConfirm: handleCancel,
      anchorElement: anchor,
    });
  };

  // Edit handler
  const onEdit = (item: ControlResultActivityListItem) => {
    if (!item.comment || !item.canEdit(userId)) return;
    handleEdit(item.comment.id, item.comment.content);
  };

  // Validate handler
  const onValidate = (item: ControlResultActivityListItem) => {
    if (!item.comment) return;
    handleValidate(item.comment.id);
  };

  // Attachment click handler
  const handleAttachmentClick = async (attachmentId: string) => {
    const commentAttachmentUrl = await getControlResultAttachmentUrl(
      controlId,
      controlResultId,
      attachmentId,
    );
    window.open(commentAttachmentUrl.url, "_blank");
  };

  return {
    // Data
    activityList,
    isLoading,
    isEditing,
    editingCommentId,

    // Handlers
    onDelete,
    onCancel,
    onEdit,
    onValidate,
    updateContent,
    handleAttachmentClick,
  };
};
